import { ArrowTrendingUpIcon, UserGroupIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import { Card, Spinner } from '@realvisor/ui';
import { useDashboardStats, useRecentLeads } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M Kč`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}K Kč`;
  }
  return `${value} Kč`;
}

function formatChange(value: number): string {
  return value >= 0 ? `+${value}%` : `${value}%`;
}

function getInitials(firstName: string, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

export default function Dashboard() {
  const { currentProject, isAllProjects } = useProject();
  const projectId = isAllProjects ? undefined : currentProject?.id;
  
  const { data: stats, isLoading: statsLoading } = useDashboardStats(projectId);
  const { data: recentLeads, isLoading: leadsLoading } = useRecentLeads(5, projectId);

  const statItems = [
    { 
      name: 'Celkem leadů', 
      value: stats?.totalLeads?.toLocaleString() || '0', 
      change: formatChange(stats?.leadsChange || 0), 
      icon: UserGroupIcon 
    },
    { 
      name: 'Nové tento měsíc', 
      value: stats?.newLeadsThisMonth?.toString() || '0', 
      change: formatChange(stats?.newLeadsChange || 0), 
      icon: ArrowTrendingUpIcon 
    },
    { 
      name: 'Hodnota pipeline', 
      value: formatCurrency(stats?.pipelineValue || 0), 
      change: formatChange(stats?.valueChange || 0), 
      icon: CurrencyDollarIcon 
    },
    { 
      name: 'Průměrná doba', 
      value: `${stats?.avgDaysToClose || 0} dní`, 
      change: formatChange(stats?.daysChange || 0), 
      icon: ClockIcon 
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">Přehled vašeho realitního byznysu</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statItems.map((stat) => (
          <Card key={stat.name} padding="md">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <stat.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.name}</p>
                {statsLoading ? (
                  <div className="h-8 w-16 animate-pulse rounded bg-gray-200" />
                ) : (
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                )}
              </div>
            </div>
            <div className="mt-4">
              <span className={`text-sm font-medium ${stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-500"> vs. minulý měsíc</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Leads by Stage - Bar Chart */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Leady podle fáze</h2>
          {statsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : stats?.leadsByStage && stats.leadsByStage.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.leadsByStage} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" />
                <YAxis 
                  type="category" 
                  dataKey="stageName" 
                  width={100}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value: number) => [`${value} leadů`, 'Počet']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Bar 
                  dataKey="count" 
                  radius={[0, 4, 4, 0]}
                >
                  {stats.leadsByStage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Žádná data k zobrazení
            </div>
          )}
        </Card>

        {/* Leads by Stage - Pie Chart */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Rozložení pipeline</h2>
          {statsLoading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : stats?.leadsByStage && stats.leadsByStage.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={stats.leadsByStage}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="count"
                  nameKey="stageName"
                  label={({ stageName, count }) => `${stageName}: ${count}`}
                  labelLine={false}
                >
                  {stats.leadsByStage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => [`${value} leadů`, 'Počet']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Žádná data k zobrazení
            </div>
          )}
        </Card>
      </div>

      {/* Recent leads */}
      <Card padding="none">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Poslední leady</h2>
          <Link to="/leads" className="text-sm text-primary-600 hover:text-primary-700">
            Zobrazit vše →
          </Link>
        </div>
        {leadsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : recentLeads && recentLeads.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {recentLeads.map((lead) => (
              <Link
                key={lead.id}
                to={`/leads/${lead.id}`}
                className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                  {getInitials(lead.contact.firstName, lead.contact.lastName)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {lead.contact.firstName} {lead.contact.lastName}
                  </p>
                  <p className="truncate text-sm text-gray-500">{lead.contact.email || 'Bez emailu'}</p>
                </div>
                <span 
                  className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                  style={{ 
                    backgroundColor: `${lead.currentStage?.color || '#6B7280'}20`, 
                    color: lead.currentStage?.color || '#6B7280' 
                  }}
                >
                  {lead.currentStage?.name || 'Bez fáze'}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">Zatím nemáte žádné leady</p>
            <Link to="/leads" className="mt-2 inline-block text-sm text-primary-600 hover:text-primary-700">
              Vytvořit první lead →
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
